import Box from '@mui/material/Box';
import { ProjectStateEvent } from '@treadinc/horizon-api-spec';

import ChildCompanyBadge from '~components/ChildCompanyBadge/ChildCompanyBadge';
import DepartmentBadge from '~components/Common/DepartmentBadge';
import PrevailingWageBadge from '~components/Common/PrevailingWageBadge';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import ProjectPricingHistoryButton from '~components/PricingHistory/ProjectPricingHistoryButton';
import ProjectStatusChip from '~components/Project/ProjectStatusChip';
import ProjectSummaryStrip from '~components/Project/ProjectSummaryStrip/ProjectSummaryStrip';
import ProjectRateCardIndicator from '~components/RateCard/ProjectRateCardIndicator';
import { Project } from '~hooks/useProjects/models';

import { Card, FlexColumn, FlexRow } from '../styled';
import Dates from './components/Dates';
import EditProjectCTA from './components/EditProjectCTA';
import InternalNotes from './components/InternalNotes';
import { ProjectActionsMenu } from './components/ProjectActionsMenu';
import ProjectResponsibleAndId from './components/ProjectResponsibleAndId';
import Subtitle from './components/Subtitle';
import Title from './components/Title';

interface ProjectsListCardProps {
  isGeneratingPdf?: boolean;
  onCopyAndEditProjectClick?: (project: Project) => void;
  onDeleteProjectClick?: (project: Project) => void;
  onEditProjectClick?: (project: Project) => void;
  onEmailQuotePdfClick?: (project: Project) => void;
  onSelectStateEvent?: (project: Project, event: ProjectStateEvent) => void;
  onToggleProjectActiveStateClick?: (project: Project) => Promise<void>;
  onViewQuotePdfClick?: (project: Project) => void;
  pendingStateEvent?: ProjectStateEvent | null;
  project: Project;
}

export default function ProjectsListCard({
  isGeneratingPdf,
  onCopyAndEditProjectClick,
  onDeleteProjectClick,
  onEditProjectClick,
  onEmailQuotePdfClick,
  onSelectStateEvent,
  onToggleProjectActiveStateClick,
  onViewQuotePdfClick,
  pendingStateEvent,
  project,
}: ProjectsListCardProps) {
  const hasMenuActions = Boolean(
    onCopyAndEditProjectClick ||
    onDeleteProjectClick ||
    onEmailQuotePdfClick ||
    onToggleProjectActiveStateClick ||
    onViewQuotePdfClick,
  );
  const hasAnyAvailableCTA = Boolean(onEditProjectClick) || hasMenuActions;

  return (
    <Card data-test-id={`project-card-${project.id}`}>
      <Box
        sx={(theme) => ({
          bgcolor: theme.brandV2.colors.treadBlue2,
          borderBottomLeftRadius: theme.spacing(),
          borderTopLeftRadius: theme.spacing(),
          flex: 0,
          minWidth: '4px',
        })}
      />

      <FlexColumn sx={{ gap: 1, flex: 1, py: 1, px: 1.5 }}>
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '4fr 2fr 2fr 3fr' }}>
          <FlexColumn sx={{ width: '100%', overflow: 'hidden' }}>
            <Title project={project} />
            <Subtitle project={project} />
            <ProjectRateCardIndicator project={project} />
            <InternalNotes project={project} />
          </FlexColumn>

          <FlexColumn sx={{ width: '100%', overflow: 'hidden' }}>
            {project.account?.name && (
              <OverflowAwareText
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadGreen,
                  fontWeight: 600,
                })}
              >
                {project.account.name}
              </OverflowAwareText>
            )}
          </FlexColumn>

          <FlexColumn sx={{ width: '100%', overflow: 'hidden' }}>
            <Dates project={project} />
          </FlexColumn>

          <FlexColumn
            sx={{
              alignItems: 'flex-end',
              gap: 1.5,
              height: '100%',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <FlexRow alignItems="center">
              <DepartmentBadge department={project.department ?? undefined} />
              <ChildCompanyBadge entity={project} sx={{ mt: 0 }} />
              {project.prevailingWage && <PrevailingWageBadge />}
              <ProjectStatusChip
                onSelectEvent={
                  onSelectStateEvent
                    ? (event) => onSelectStateEvent(project, event)
                    : undefined
                }
                pendingEvent={pendingStateEvent}
                project={project}
              />

              <ProjectPricingHistoryButton
                analyticsSurface="project_list"
                project={project}
              />

              {hasAnyAvailableCTA && (
                <FlexRow alignItems="center" sx={{ gap: 0 }}>
                  {onEditProjectClick && (
                    <EditProjectCTA onClick={() => onEditProjectClick(project)} />
                  )}

                  {hasMenuActions && (
                    <ProjectActionsMenu
                      isGeneratingPdf={isGeneratingPdf}
                      onCopyAndEditProjectClick={onCopyAndEditProjectClick}
                      onDeleteProjectClick={onDeleteProjectClick}
                      onEmailQuotePdfClick={onEmailQuotePdfClick}
                      onToggleProjectActiveStateClick={onToggleProjectActiveStateClick}
                      onViewQuotePdfClick={onViewQuotePdfClick}
                      project={project}
                    />
                  )}
                </FlexRow>
              )}
            </FlexRow>

            <Box sx={{ mt: 'auto' }}>
              <ProjectResponsibleAndId project={project} />
            </Box>
          </FlexColumn>
        </Box>

        <ProjectSummaryStrip
          materialTotals={project.projectMaterialTotals}
          projectSummary={project.projectSummary}
        />
      </FlexColumn>
    </Card>
  );
}
